import { useEffect, useState, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import STPerformanceUnclassified from "./STPerformanceUnclassified/STPerformanceUnclassifiedTable";
import STMissingCrossNegatives from "./STMissingCrossNegatives/STMissingCrossNegativesTable";
import ContradictingSTTable from "./ContradictingST/ContradictingSTTable";

import {
  resetCapturedByMultipleKeywordsSearchTerm,
  resetContradictingSTDefinitionSearchTerm,
  resetIdenticalToExactKeyword,
  resetMissingCrossNegativesSearchTerm,
  resetPerformanceUnclassifiedSearchTerm,
} from "../../../../../../../../features/searchTermTabSlice";
import MultiLoadingSnackbar from "../../../../MultiLoadingSnackbar";
import IdenticalToExactKeywordTable from "./IdenticalToExactKeyword/IdenticalToExactKeywordTable";
import CapturedByMultipleKeywordsTable from "./CapturedByMultipleKeywords/CapturedByMultipleKeywordsTable";

interface SnackbarData {
  id: string;
  message: string;
  subMessage?: string;
}
const accordionStyles = {
  "&.Mui-expanded": { margin: 0 },
  "& .MuiAccordionSummary-root": { minHeight: "48px" },
  "& .MuiAccordionSummary-root.Mui-expanded": { minHeight: "48px" },
  "& .MuiAccordionSummary-content": { margin: 0 },
  "& .MuiAccordionSummary-content.Mui-expanded": { margin: 0 },
};

// Custom hook for smooth accordion management
const useAccordionManager = (accordionConfig: any[]) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expanded, setExpanded] = useState<string | false>(false);

  // Sync with URL
  useEffect(() => {
    const urlAccordion = searchParams.get("accordion");
    const match = accordionConfig.find((a) => a.urlKey === urlAccordion);
    setExpanded(match ? match.id : false);
  }, [searchParams, accordionConfig]);

  const handleToggle = useCallback(
    (accordionId: string, isExpanding: boolean) => {
      const current = accordionConfig.find((a) => a.id === accordionId);
      if (!current) return;

      // Step 1: UI update (instant)
      setExpanded(isExpanding ? accordionId : false);

      // Step 2: URL update (fast)
      const params = new URLSearchParams(searchParams);

      if (isExpanding) {
        params.set("accordion", current.urlKey);
        // Clear previous accordion params
        const prev = accordionConfig.find((a) => a.id === expanded);
        prev?.urlParamsToClear?.forEach((key: any) => params.delete(key));
      } else {
        params.delete("accordion");
        current.urlParamsToClear?.forEach((key: any) => params.delete(key));
      }

      setSearchParams(params, { replace: true });

      // Step 3: Redux cleanup (async, after animation)
      requestAnimationFrame(() => {
        if (isExpanding && expanded) {
          const prev = accordionConfig.find((a) => a.id === expanded);
          prev?.clearAction?.();
        } else if (!isExpanding) {
          current.clearAction?.();
        }
      });
    },
    // eslint-disable-next-line
    [accordionConfig, expanded, searchParams]
  );

  return { expanded, handleToggle };
};

const SearchTermTabAccordions = () => {
  const dispatch = useDispatch();
  const [snackbars, setSnackbars] = useState<SnackbarData[]>([]);

  const {
    stWithPerformanceUnClassified,
    stMissingCrossNegatives,
    contradictingSTDefinition,
    confirmLoaderForContradictingSTDefinition,
    stIdenticalToExactKeyword,
    capturedByMultipleKeywords,
  } = useSelector((state: any) => state.searchTermTab);

  // Configuration - easy to modify
  const accordions = [
    {
      id: "panel1",
      title: "Preforming Search Terms Without Classification",
      description:
        "Search terms that have generated traffic (impressions, clicks, conversions) but do not have a defined classification relative to keywords",
      count: Array.isArray(stWithPerformanceUnClassified)
        ? stWithPerformanceUnClassified.length
        : 0,
      component: STPerformanceUnclassified,
      urlKey: "performingWithoutClassification",
      urlParamsToClear: ["filters", "page", "sort", "search"],
      clearAction: () => dispatch(resetPerformanceUnclassifiedSearchTerm()), // Add if you have a clear action
    },
    {
      id: "panel2",
      title: "Exclusive Search Terms Missing Cross-Negatives",
      description:
        "Exclusive search terms that are not defined as negatives in other ad groups",
      count: Array.isArray(stMissingCrossNegatives)
        ? stMissingCrossNegatives.length
        : 0,
      component: STMissingCrossNegatives,
      urlKey: "missingCrossNegatives",
      urlParamsToClear: ["filters", "page", "sort", "search"],
      clearAction: () => dispatch(resetMissingCrossNegativesSearchTerm()),
    },
    {
      id: "panel3",
      title: "Contradicting Search Terms Definitions",
      description:
        "Exclusive/Allowed search terms that are marked as negative in same ad group",
      count: Array.isArray(contradictingSTDefinition)
        ? contradictingSTDefinition.length
        : 0,
      component: ContradictingSTTable,
      urlKey: "contradictingDefinitions",
      urlParamsToClear: ["filters", "page", "sort", "search"],
      clearAction: () => dispatch(resetContradictingSTDefinitionSearchTerm()), // must have clear action
    },
    {
      id: "panel4",
      title: "Identical to exact keyword yet undefined as negative",
      description:
        "Search terms that matches an exact keyword and are not defined as a negative in other ad groups",
      count: Array.isArray(stIdenticalToExactKeyword)
        ? stIdenticalToExactKeyword.length
        : 0,
      component: IdenticalToExactKeywordTable,
      urlKey: "identicalToKeyword",
      urlParamsToClear: ["filters", "page", "sort", "search"],
      clearAction: () => dispatch(resetIdenticalToExactKeyword()), // must have clear action
    },
    {
      id: "panel5",
      title: "Captured by multiple keywords in different ad groups",
      description:
        "Search Terms Marked as Allowed, Matched by Multiple Keywords in Other Groups",
      count: Array.isArray(capturedByMultipleKeywords)
        ? capturedByMultipleKeywords.length
        : 0,
      component: CapturedByMultipleKeywordsTable,
      urlKey: "capturedByMultiple",
      urlParamsToClear: ["filters", "page", "sort", "search"],
      clearAction: () => dispatch(resetCapturedByMultipleKeywordsSearchTerm()),
    },
  ];

  const { expanded, handleToggle } = useAccordionManager(accordions);

  // loading snackbar for long api response (around 1.35 sec to remove) (Contradicting ST definition removal)
  useEffect(() => {
    const active = [];

    if (confirmLoaderForContradictingSTDefinition) {
      active.push({
        id: "remove-negative-contradicting",
        message:
          "Removing negative search terms from contradicting definitions...",
        subMessage: "This may take several minutes, please wait.",
      });
    }

    setSnackbars(active);
  }, [confirmLoaderForContradictingSTDefinition]);

  return (
    <>
      {/* Loading top right for long api response */}
      <MultiLoadingSnackbar snackbars={snackbars} />
      <Box p={2}>
        <Stack direction="column" alignItems="left" padding="8px">
          <Typography variant="h5">Anomalies</Typography>
          <Typography variant="body2" gutterBottom color="text.secondary">
            Identify and resolve account-level anomalies in Negative Search Term
            configurations
          </Typography>
        </Stack>

        {accordions.map(
          ({ id, title, description, count, component: Component }) => (
            <Accordion
              key={id}
              expanded={expanded === id}
              onChange={(_, isExpanded) => handleToggle(id, isExpanded)}
              sx={accordionStyles}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${id}-content`}
                id={`${id}-header`}
              >
                <Typography
                  component="span"
                  sx={{ width: "33%", flexShrink: 0 }}
                >
                  {title} – {count}
                </Typography>
                <Typography component="span" sx={{ color: "text.secondary" }}>
                  {description}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Component />
              </AccordionDetails>
            </Accordion>
          )
        )}
      </Box>
    </>
  );
};

export default SearchTermTabAccordions;
